// Dependency-free pinch-zoom / pan for a static image inside a viewport
// (the play-shell map view, gameplay 2027). Two-finger pinch, one-finger /
// mouse pan, wheel zoom and double-tap zoom. No pins, no interaction —
// v1 treats the course map as a plain image.
//
// Usage (Blazor interop): window.tcPinchZoom.init('.js-pinch-zoom', { cover: true });
// The viewport element must contain an <img class="c-play-map__img">
// (or any single <img>).
//
// With `cover: true` (full-page map) the image is a background layer over the
// whole viewport. Fixed overlays (header, stats bar, tab bar, hint —
// `topOverlays` / `bottomOverlays` selectors) are measured live: the map
// opens fitted so the whole image is visible between them, zooms in up to
// 4x the cover scale, and every edge of the image can be panned out from
// under them. Without `cover`, the image is clamped to the viewport as
// before.
//
// `swipeUp` (full-screen image viewer): a one-finger swipe upwards while the
// image is not zoomed in calls back into .NET (`ref.invokeMethodAsync(method)`)
// — used to close the viewer.

const DOUBLE_TAP_MS = 300;

type PinchZoomOptions = {
	/** Full-viewport background layer; opens fitted to the area between the overlays. */
	cover?: boolean;
	/** Fixed overlays covering the top of the viewport (e.g. '.c-header, .c-play-stats'). */
	topOverlays?: string;
	/** Fixed overlays covering the bottom of the viewport (e.g. '.c-play-tabbar'). */
	bottomOverlays?: string;
	/** Called on a one-finger swipe up while not zoomed in (Blazor DotNetObjectReference + method name). */
	swipeUp?: { ref: { invokeMethodAsync(method: string): Promise<unknown> }; method: string };
};

// a swipe up closes: at least this far up, and mostly vertical
const SWIPE_UP_MIN = 80;

// breathing room between the map edge and an overlay when panned/zoomed to the limit
const OVERLAY_GAP = 8;

// cover mode re-fits after these delays, for overlays that render after the map
const REFIT_DELAYS_MS = [250, 1000];

class PinchZoom {
	private readonly viewport: HTMLElement;
	private readonly img: HTMLImageElement;
	private readonly cover: boolean;
	private readonly topOverlays?: string;
	private readonly bottomOverlays?: string;
	private readonly swipeUp?: PinchZoomOptions['swipeUp'];
	private readonly pointers = new Map<number, { x: number; y: number }>();

	// one-finger gesture tracking for swipeUp
	private gestureStart: { x: number; y: number } | null = null;
	private gestureMultiTouch = false;

	private minScale = 1;
	private coverScale = 1;
	private maxScale = 4;
	private insetTop = 0;
	private insetBottom = 0;
	private scale = 1;
	private tx = 0;
	private ty = 0;

	private pinchStartDistance = 0;
	private pinchStartScale = 1;
	private lastTapAt = 0;
	private refitTimers: number[] = [];

	private readonly onPointerDown = (e: PointerEvent) => this.pointerDown(e);
	private readonly onPointerMove = (e: PointerEvent) => this.pointerMove(e);
	private readonly onPointerUp = (e: PointerEvent) => this.pointerUp(e);
	private readonly onWheel = (e: WheelEvent) => this.wheel(e);
	private readonly onResize = () => this.applyCover();
	private readonly onImgLoad = () => this.applyCover();

	constructor(viewport: HTMLElement, options?: PinchZoomOptions) {
		this.viewport = viewport;
		this.cover = options?.cover === true;
		this.topOverlays = options?.topOverlays;
		this.bottomOverlays = options?.bottomOverlays;
		this.swipeUp = options?.swipeUp;

		const img = viewport.querySelector('img');
		if (!img) {
			throw new Error('tcPinchZoom: viewport contains no <img>');
		}
		this.img = img as HTMLImageElement;

		viewport.addEventListener('pointerdown', this.onPointerDown);
		viewport.addEventListener('pointermove', this.onPointerMove);
		viewport.addEventListener('pointerup', this.onPointerUp);
		viewport.addEventListener('pointercancel', this.onPointerUp);
		viewport.addEventListener('wheel', this.onWheel, { passive: false });

		if (this.cover) {
			window.addEventListener('resize', this.onResize);
			if (this.img.complete) {
				this.applyCover();
			} else {
				this.img.addEventListener('load', this.onImgLoad, { once: true });
			}
			// the stats bar renders after the map — re-fit once it is there (no-op once zoomed in)
			this.refitTimers = REFIT_DELAYS_MS.map((ms) => window.setTimeout(this.onResize, ms));
		}
	}

	destroy(): void {
		this.viewport.removeEventListener('pointerdown', this.onPointerDown);
		this.viewport.removeEventListener('pointermove', this.onPointerMove);
		this.viewport.removeEventListener('pointerup', this.onPointerUp);
		this.viewport.removeEventListener('pointercancel', this.onPointerUp);
		this.viewport.removeEventListener('wheel', this.onWheel);
		this.img.removeEventListener('load', this.onImgLoad);
		window.removeEventListener('resize', this.onResize);
		this.refitTimers.forEach((t) => window.clearTimeout(t));
		this.pointers.clear();
	}

	// How far the fixed overlays reach into the viewport (top/bottom), in px.
	private measureInsets(): void {
		const vp = this.viewport.getBoundingClientRect();
		let top = 0;
		let bottom = 0;

		if (this.topOverlays) {
			document.querySelectorAll<HTMLElement>(this.topOverlays).forEach((el) => {
				const r = el.getBoundingClientRect();
				if (r.height > 0 && r.top < vp.top + vp.height / 2) {
					top = Math.max(top, r.bottom - vp.top + OVERLAY_GAP);
				}
			});
		}

		if (this.bottomOverlays) {
			document.querySelectorAll<HTMLElement>(this.bottomOverlays).forEach((el) => {
				const r = el.getBoundingClientRect();
				if (r.height > 0 && r.bottom > vp.top + vp.height / 2) {
					bottom = Math.max(bottom, vp.bottom - r.top + OVERLAY_GAP);
				}
			});
		}

		this.insetTop = Math.max(0, top);
		this.insetBottom = Math.max(0, bottom);
	}

	// Cover mode: opens (and re-fits, when still at the fitted view) with the
	// whole image visible between the overlays — that is also the furthest it
	// zooms out. The img is laid out at 100% viewport width, so only the height
	// decides the scales.
	private applyCover(recenter = true): void {
		const w = this.img.offsetWidth;
		const h = this.img.offsetHeight;
		const vw = this.viewport.clientWidth;
		const vh = this.viewport.clientHeight;
		if (!w || !h || !vw || !vh) {
			return;
		}

		this.measureInsets();

		const wasAtFit = Math.abs(this.scale - this.minScale) < 0.001;
		const visibleHeight = Math.max(1, vh - this.insetTop - this.insetBottom);

		this.coverScale = Math.max(1, vh / h);
		this.minScale = Math.min(1, visibleHeight / h, vw / w);
		this.maxScale = this.coverScale * 4;

		if ((recenter && wasAtFit) || this.scale < this.minScale) {
			this.scale = this.minScale;
			this.tx = (vw - w * this.scale) / 2;
			this.ty = (vh - h * this.scale) / 2;
		}

		this.apply();
	}

	private apply(): void {
		this.clamp();
		this.img.style.transform = `translate(${this.tx}px, ${this.ty}px) scale(${this.scale})`;
	}

	// Per axis: a scaled image larger than the visible range [start, end] may
	// be panned until each of its edges reaches that range's edge; a smaller
	// one is centered in it. Without cover the range is the whole viewport.
	private static clampAxis(t: number, size: number, start: number, end: number): number {
		const range = end - start;
		if (size <= range) {
			return start + (range - size) / 2;
		}
		return Math.min(start, Math.max(end - size, t));
	}

	private clamp(): void {
		this.scale = Math.min(this.maxScale, Math.max(this.minScale, this.scale));

		const w = this.img.offsetWidth * this.scale;
		const h = this.img.offsetHeight * this.scale;
		const vw = this.viewport.clientWidth;
		const vh = this.viewport.clientHeight;

		this.tx = PinchZoom.clampAxis(this.tx, w, 0, vw);
		this.ty = PinchZoom.clampAxis(this.ty, h, this.insetTop, vh - this.insetBottom);
	}

	// Zooms so the viewport point (px, py) stays fixed on screen.
	private zoomAt(px: number, py: number, newScale: number): void {
		const clamped = Math.min(this.maxScale, Math.max(this.minScale, newScale));
		const factor = clamped / this.scale;
		this.tx = px - (px - this.tx) * factor;
		this.ty = py - (py - this.ty) * factor;
		this.scale = clamped;
		this.apply();
	}

	private localPoint(e: { clientX: number; clientY: number }): { x: number; y: number } {
		const rect = this.viewport.getBoundingClientRect();
		return { x: e.clientX - rect.left, y: e.clientY - rect.top };
	}

	private pinchDistance(): number {
		const [a, b] = [...this.pointers.values()];
		return Math.hypot(a.x - b.x, a.y - b.y);
	}

	private pointerDown(e: PointerEvent): void {
		if (this.cover && this.pointers.size === 0) {
			// overlays may have appeared/changed since the last layout (stats bar loads late)
			this.applyCover(false);
		}

		this.viewport.setPointerCapture(e.pointerId);
		this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

		if (this.pointers.size === 1) {
			this.gestureStart = { x: e.clientX, y: e.clientY };
			this.gestureMultiTouch = false;
		} else {
			this.gestureMultiTouch = true;
		}

		if (this.pointers.size === 2) {
			this.pinchStartDistance = this.pinchDistance();
			this.pinchStartScale = this.scale;
		} else if (this.pointers.size === 1) {
			const now = Date.now();
			if (now - this.lastTapAt < DOUBLE_TAP_MS) {
				const p = this.localPoint(e);
				// toggle between the fitted view and a 2.5x close-up
				const zoomedIn = this.scale > this.minScale + 0.001;
				this.zoomAt(p.x, p.y, zoomedIn ? this.minScale : this.minScale * 2.5);
				this.lastTapAt = 0;
			} else {
				this.lastTapAt = now;
			}
		}
	}

	private pointerMove(e: PointerEvent): void {
		const previous = this.pointers.get(e.pointerId);
		if (!previous) {
			return;
		}

		if (this.pointers.size === 2) {
			this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

			const [a, b] = [...this.pointers.values()];
			const rect = this.viewport.getBoundingClientRect();
			const midX = (a.x + b.x) / 2 - rect.left;
			const midY = (a.y + b.y) / 2 - rect.top;

			const distance = this.pinchDistance();
			if (this.pinchStartDistance > 0) {
				this.zoomAt(midX, midY, this.pinchStartScale * (distance / this.pinchStartDistance));
			}
		} else if (this.pointers.size === 1) {
			this.tx += e.clientX - previous.x;
			this.ty += e.clientY - previous.y;
			this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
			this.apply();
		}
	}

	private pointerUp(e: PointerEvent): void {
		this.pointers.delete(e.pointerId);
		if (this.pointers.size < 2) {
			this.pinchStartDistance = 0;
		}

		if (this.pointers.size === 0) {
			this.detectSwipeUp(e);
			this.gestureStart = null;
		}
	}

	// A one-finger flick upwards while the image sits at its fitted size (not
	// zoomed in, so the drag was not a pan of a close-up).
	private detectSwipeUp(e: PointerEvent): void {
		if (!this.swipeUp || !this.gestureStart || this.gestureMultiTouch || e.type === 'pointercancel') {
			return;
		}

		if (this.scale > this.minScale + 0.001) {
			return;
		}

		const dx = e.clientX - this.gestureStart.x;
		const dy = e.clientY - this.gestureStart.y;
		if (dy <= -SWIPE_UP_MIN && Math.abs(dx) < Math.abs(dy) * 0.6) {
			this.swipeUp.ref.invokeMethodAsync(this.swipeUp.method).catch(() => undefined);
		}
	}

	private wheel(e: WheelEvent): void {
		e.preventDefault();
		const p = this.localPoint(e);
		const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
		this.zoomAt(p.x, p.y, this.scale * factor);
	}
}

const instances = new Map<Element, PinchZoom>();

export const tcPinchZoom = {
	init(selector: string, options?: PinchZoomOptions): void {
		document.querySelectorAll<HTMLElement>(selector).forEach((el) => {
			if (!instances.has(el)) {
				try {
					instances.set(el, new PinchZoom(el, options));
				} catch {
					// no <img> yet (e.g. still loading) — caller may retry
				}
			}
		});
	},

	destroy(selector: string): void {
		document.querySelectorAll<HTMLElement>(selector).forEach((el) => {
			instances.get(el)?.destroy();
			instances.delete(el);
		});
	},
};
